// @flow

import * as React from "react";
// import { useSelector } from "react-redux";
import {
    ParallaxProvider,
    Parallax,
    withController,
    useController,
} from "react-scroll-parallax";

import { PrimaryNavPad } from "../organisms/PrimaryNav";
import { type ImageType, type LinkType } from "../../types";

import Image from "../atoms/Image";
import Icon from "../atoms/Icon";
import LinkHandler from "../atoms/LinkHandler";

import { ReactComponent as Triangle } from "../../../assets/images/shape-triangle.svg";
import { ReactComponent as Circle } from "../../../assets/images/shape-circle.svg";
import { ReactComponent as Ring } from "../../../assets/images/shape-ring-fill.svg";

type PropsType = {
    theme: "purple" | "orange" | "green" | "teal" | "pink" | "red" | "blue",
    heading: string,
    eyebrow?: string,
    link?: LinkType,
    image: ImageType,
    quick_links?: [],
};

const HeroHome = (props: PropsType): React.Node => {
    return (
        <ParallaxProvider>
            <HeroHomeInner {...props} />
        </ParallaxProvider>
    );
};

const HeroHomeInner = (props: PropsType): React.Node => {
    const { parallaxController } = useController();
    const baseClassName = "hero-home";
    const classes = [baseClassName];

    React.useEffect(() => {
        parallaxController.update();
        setTimeout(() => {
            parallaxController.update();
        }, 500);
    }, []); // eslint-disable-line

    if (props.theme) {
        classes.push(`${baseClassName}--${props.theme}`);
    }

    return (
        <div className={classes.join(" ")}>
            <div className="hero-home__inner">
                <PrimaryNavPad />
                <div className="hero-home__container container-fluid">
                    {/* Design elements */}
                    <Parallax y={[0, 0]} className="hero-home__circle-small">
                        <Circle />
                    </Parallax>

                    <Parallax
                        y={["-20%", "30%"]}
                        className="hero-home__circle-big"
                    >
                        <Circle />
                    </Parallax>

                    <Parallax
                        y={["350%", "-100%"]}
                        className="hero-home__triangle"
                    >
                        <Triangle />
                    </Parallax>

                    <Parallax y={["55%", "-50%"]} className="hero-home__row">
                        <div className="row">
                            <div className="hero-home__col col-xs-7 col-lg-8">
                                {props.eyebrow ? (
                                    <div className="hero-home__eyebrow">
                                        {props.eyebrow}
                                    </div>
                                ) : null}

                                <h1 className="hero-home__heading">
                                    {props.heading}
                                </h1>

                                {props.link ? (
                                    <LinkHandler
                                        to={props.link.url}
                                        className="hero-home__cta"
                                    >
                                        <span className="text">
                                            {props.link.title}
                                        </span>
                                        <Icon name="arrow-right" />
                                    </LinkHandler>
                                ) : null}
                            </div>

                            <div className="col-xs-6">
                                <div className="hero-home__image">
                                    <Image
                                        image={props.image}
                                        size={"large"}
                                        alt={
                                            props.image.alt ||
                                            props.image.caption
                                        }
                                        aspectRatio="1:1"
                                    />
                                </div>
                            </div>
                        </div>
                    </Parallax>
                </div>

                {/* Quick links */}
                {props.quick_links && props.quick_links.length ? (
                    <div className="hero-home__quick-links">
                        <div className="container-fluid">
                            <div style={{ position: "relative" }}>
                                <Parallax
                                    y={["45%", "-90%"]}
                                    className="hero-home__ring"
                                >
                                    <Ring />
                                </Parallax>
                            </div>
                        </div>

                        <div className="hero-home__quick-links__scroller">
                            <div className="container-fluid container-fluid--bleed-right">
                                <div className="hero-home__quick-links__inner">
                                    {props.quick_links.map((quickLink, i) => {
                                        return (
                                            <LinkHandler
                                                key={i}
                                                to={quickLink.link.url}
                                                className="hero-home__quick-links__item"
                                            >
                                                <div className="hero-home__quick-links__item__inner">
                                                    <Image
                                                        image={quickLink.icon}
                                                        size="small"
                                                    />
                                                    <span className="text">
                                                        {quickLink.link.title}
                                                    </span>
                                                </div>
                                            </LinkHandler>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    </div>
                ) : null}
            </div>
        </div>
    );
};

HeroHome.defaultProps = {
    theme: "purple",
};

export default withController(HeroHome);
